import { Head, Link } from "@inertiajs/react";
import MainLayout from "../../components/MainLayout";
import Container from "../../components/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, Eye, Users, Award, Star, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";

import { StoreSettings } from "../../types";

interface Owner {
  name?: string;
  message?: string;
  photo_url?: string;
}

interface Client {
  name: string;
  description: string;
  image_url?: string;
}

interface AboutData {
  description?: string;
  mission?: string;
  vision?: string;
  history?: string;
  owner?: Owner;
  clients: Client[];
}

interface AboutProps {
  about: AboutData;
  store_settings: StoreSettings;
}

const About = ({ about, store_settings }: AboutProps) => {
  const stats = [
    { number: "10,000+", label: "Clientes Satisfechos", icon: Users },
    { number: "50,000+", label: "Productos Vendidos", icon: Award },
    { number: "5", label: "Años de Experiencia", icon: Star },
    { number: "24/7", label: "Soporte Disponible", icon: Target },
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Head title={`Nosotros - ${store_settings?.store_name}`} />

      {/* Hero Section */}
      <motion.section
        className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-cyan-900 to-green-900 text-white"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <Container className="relative py-32 lg:py-48 text-center">
          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-gray-400 via-gray-200 to-gray-400 bg-clip-text text-transparent"
            variants={fadeIn}
          >
            Sobre {store_settings?.store_name}
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-cyan-300 max-w-3xl mx-auto mb-10"
            variants={fadeIn}
          >
            Innovación y excelencia en cada producto. Descubre cómo nuestra
            pasión por la tecnología nos impulsa a ofrecerte lo mejor.
          </motion.p>
          <motion.div variants={fadeIn} className="flex flex-col items-center">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 transform hover:scale-105"
              onClick={() =>
                document
                  .getElementById("history")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Nuestra Trayectoria
            </Button>
            <ChevronDown className="mt-4 h-6 w-6 text-cyan-300 animate-bounce" />
          </motion.div>
        </Container>
      </motion.section>

      {/* About Description */}
      {about?.description && (
        <motion.section
          className="py-24 bg-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4 text-gray-800">
                Nuestra Esencia
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-500 to-green-500 mx-auto rounded-full mb-12"></div>
              <p className="text-xl text-gray-600 leading-relaxed">
                {about.description}
              </p>
            </div>
          </Container>
        </motion.section>
      )}

      {/* History Section */}
      {about?.history && (
        <motion.section
          id="history"
          className="py-24 bg-gray-50"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4 text-gray-800">
                Nuestra Historia
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-500 to-green-500 mx-auto rounded-full mb-12"></div>
              <p className="text-xl text-gray-600 leading-relaxed">
                {about.history}
              </p>
            </div>
          </Container>
        </motion.section>
      )}

      {/* Mission and Vision */}
      {(about?.mission || about?.vision) && (
        <motion.section
          className="py-24 bg-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <Container>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {about.mission && (
                <motion.div variants={fadeIn}>
                  <Card className="bg-gradient-to-br from-cyan-50 to-green-50 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardHeader className="items-center text-center">
                      <div className="p-4 bg-gradient-to-br from-cyan-500 to-green-500 rounded-full mb-4">
                        <Target className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-3xl font-bold text-gray-800">
                        Misión
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-center text-lg text-gray-700">
                        {about.mission}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
              {about.vision && (
                <motion.div variants={fadeIn}>
                  <Card className="bg-gradient-to-br from-cyan-50 to-green-50 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardHeader className="items-center text-center">
                      <div className="p-4 bg-gradient-to-br from-cyan-500 to-green-500 rounded-full mb-4">
                        <Eye className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-3xl font-bold text-gray-800">
                        Visión
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-center text-lg text-gray-700">
                        {about.vision}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
          </Container>
        </motion.section>
      )}

      {/* Owner Section */}
      {about?.owner && (about.owner.name || about.owner.message) && (
        <motion.section
          className="py-24 bg-gray-50"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn}
        >
          <Container>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800">
                  Gerente General
                </h2>
                <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-500 to-green-500 mx-auto rounded-full mt-4"></div>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-12">
                {about.owner.photo_url && (
                  <motion.div variants={fadeIn} className="flex-shrink-0">
                    <div className="w-64 h-64 rounded-full p-1 bg-gradient-to-r from-gray-400 via-gray-200 to-gray-400 shadow-2xl">
                      <img
                        src={about.owner.photo_url}
                        alt={about.owner.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                  </motion.div>
                )}
                <motion.div variants={fadeIn} className="flex-1">
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    {about.owner.name}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed italic">
                    "{about.owner.message}"
                  </p>
                </motion.div>
              </div>
            </div>
          </Container>
        </motion.section>
      )}

      {/* Clients Section */}
      {about?.clients && about.clients.length > 0 && (
        <motion.section
          className="py-24 bg-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Container>
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800">
                Nuestros Clientes
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-500 to-green-500 mx-auto rounded-full mt-4"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {about.clients.map((client, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col items-center text-center"
                >
                  {client.image_url && (
                    <img
                      src={client.image_url}
                      alt={client.name}
                      className="w-24 h-24 rounded-full object-contain bg-white p-2 shadow-lg mb-4 transition-transform duration-300 hover:scale-110"
                    />
                  )}
                  <h3 className="text-lg font-bold text-gray-800">
                    {client.name}
                  </h3>
                </motion.div>
              ))}
            </div>
          </Container>
        </motion.section>
      )}

      {/* Stats Section */}
      <motion.section
        className="py-24 bg-gradient-to-r from-gray-800 via-cyan-800 to-green-800 text-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon className="h-12 w-12 text-cyan-400 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white">
                    {stat.number}
                  </div>
                  <div className="text-lg text-cyan-300">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="py-24 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={fadeIn}
      >
        <Container className="text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            ¿Listo para el Siguiente Nivel?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Explora nuestra selección de productos y descubre por qué somos la
            elección preferida de los entusiastas de la tecnología.
          </p>
          <Link href="/products">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Ver Productos
            </Button>
          </Link>
        </Container>
      </motion.section>
    </>
  );
};

About.layout = (page: any) => (
  <MainLayout store_settings={page.props.store_settings} exclusiveProducts={[]}>
    {page}
  </MainLayout>
);

export default About;
